import { FC } from 'react';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import styles from './PunchupBoxOffice.module.css';

// Shapes returned by POST /public/ticketing/addons/applicable (gorm.Model → `ID`).
export interface AddonVariation {
	ID: number;
	value: string;
	optional_price?: number | null;
}

export interface AddonCustomization {
	ID: number;
	name: string;
	variations: AddonVariation[];
}

export interface BoxOfficeAddon {
	ID: number;
	name: string;
	description?: string;
	default_price: number;
	customizations?: AddonCustomization[];
}

export interface ApplicableAddon {
	addon: BoxOfficeAddon;
	max_quantity: number;
	required_for_all: boolean;
}

// addonId → { quantity, variations: customizationId → variationId }.
export interface AddonSelection {
	quantity: number;
	variations: Record<number, number>;
}
export type AddonSelections = Record<number, AddonSelection>;

/** Build the calculate-totals / purchase `addons_with_variations` array from the selection map. */
export const toAddonsPayload = (
	selections: AddonSelections
): Array<{ addon_id: number; quantity: number; selected_variations: number[] }> =>
	Object.entries(selections)
		.filter(([, sel]) => sel.quantity > 0)
		.map(([addonId, sel]) => ({
			addon_id: Number(addonId),
			quantity: sel.quantity,
			selected_variations: Object.values(sel.variations),
		}));

interface BoxOfficeAddonsProps {
	applicableAddons: ApplicableAddon[];
	selections: AddonSelections;
	onChange: (addonId: number, selection: AddonSelection) => void;
	formatPrice: (value: number) => string;
	disabled?: boolean;
}

export const BoxOfficeAddons: FC<BoxOfficeAddonsProps> = ({
	applicableAddons,
	selections,
	onChange,
	formatPrice,
	disabled,
}) => {
	const { t } = useTranslation();

	return (
		<div className={styles.addonList}>
			{applicableAddons.map(({ addon, max_quantity, required_for_all }) => {
				const selection = selections[addon.ID] ?? { quantity: 0, variations: {} };
				const setQuantity = (next: number) => {
					const quantity = Math.max(0, Math.min(next, max_quantity));
					// When the add-on becomes active, make sure every customization has a chosen variation
					// (default to the first) so the backend can resolve it to a concrete product.
					const variations = { ...selection.variations };
					if (quantity > 0) {
						(addon.customizations ?? []).forEach((customization) => {
							if (variations[customization.ID] === undefined && customization.variations[0]) {
								variations[customization.ID] = customization.variations[0].ID;
							}
						});
					}
					onChange(addon.ID, { quantity, variations });
				};
				const setVariation = (customizationId: number, variationId: number) =>
					onChange(addon.ID, { ...selection, variations: { ...selection.variations, [customizationId]: variationId } });

				const stepperDisabled = disabled || max_quantity === 0;

				return (
					<div
						key={addon.ID}
						className={`${styles.ticketCard}${selection.quantity > 0 ? ` ${styles.ticketCardActive}` : ''}`}
					>
						<div className={styles.addonBody}>
							<div className={styles.ticketInfo}>
								<h3 className={styles.ticketName}>{addon.name}</h3>
								{addon.description && <p className={styles.ticketDescription}>{addon.description}</p>}

								{(addon.customizations ?? []).map((customization) => (
									<div key={customization.ID} className={styles.addonCustomization}>
										<span className={styles.addonCustomizationLabel}>{customization.name}</span>
										<div className={styles.addonVariations}>
											{customization.variations.map((variation) => {
												const active = selection.variations[customization.ID] === variation.ID;
												return (
													<button
														key={variation.ID}
														type="button"
														className={active ? styles.addonVariationActive : styles.addonVariation}
														onClick={() => setVariation(customization.ID, variation.ID)}
														disabled={disabled}
													>
														{variation.value}
														{variation.optional_price ? ` (+${formatPrice(variation.optional_price)})` : ''}
													</button>
												);
											})}
										</div>
									</div>
								))}
							</div>

							<div className={styles.addonControls}>
								<span className={styles.ticketPrice}>{formatPrice(addon.default_price)}</span>
								{required_for_all && (
									<span className={styles.soldOutBadge}>{t('boxOffice-addonRequired', 'Required')}</span>
								)}
								<div className={styles.stepper}>
									<button
										type="button"
										className={styles.stepperButton}
										onClick={() => setQuantity(selection.quantity - 1)}
										disabled={stepperDisabled || selection.quantity === 0}
										aria-label={t('decrement', 'Decrement')}
									>
										<Minus size={16} aria-hidden />
									</button>
									<span className={styles.stepperCount}>{selection.quantity}</span>
									<button
										type="button"
										className={styles.stepperButton}
										onClick={() => setQuantity(selection.quantity + 1)}
										disabled={stepperDisabled || selection.quantity >= max_quantity}
										aria-label={t('increment', 'Increment')}
									>
										<Plus size={16} aria-hidden />
									</button>
								</div>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};
